import type { JSONContent } from '@tiptap/react';

// Transform a ProseMirror JSON document by replacing newline-like tokens in text nodes
// with hardBreak nodes.
//
// Behavior
// - Normalizes literal token "\n" to real newline characters
// - Splits text nodes on newline characters
// - Inserts a { type: 'hardBreak' } node between each split segment
// - Preserves text marks on the split segments
//
// Examples
// 1) "hello\nworld" =>
//    [{text:"hello"}, {hardBreak}, {text:"world"}]
//
// 2) "a\\nb" (contains a literal "\n" token) =>
//    "a\nb" => [{text:"a"}, {hardBreak}, {text:"b"}]
//
// 3) "x\n\ny" (consecutive newlines) =>
//    [{text:"x"}, {hardBreak}, {hardBreak}, {text:"y"}]
//
// 4) Marks are preserved on split text:
//    [{text:"x\ny", marks:[{type:'bold'}]}] =>
//    [{text:"x", marks:[{bold}]}, {hardBreak}, {text:"y", marks:[{bold}]}]
export function normalizeNewlinesInDoc(doc: JSONContent): JSONContent {
  // Turn ["a", "b", "c"] into [{text:a}, <br>, {text:b}, <br>, {text:c}]
  const toTextAndBreakNodes = (
    segments: string[],
    marks?: JSONContent['marks'],
  ): JSONContent[] => {
    const nodes: JSONContent[] = [];
    const hasNonEmpty = segments.some((segment) => segment.length > 0);
    segments.forEach((segment, index) => {
      // Insert a break before every segment after the first; suppress when the last segment is empty and earlier segments contain text (trailing newline)
      const isTrailingEmpty =
        index === segments.length - 1 && segment.length === 0;
      const shouldInsertBreak = index > 0 && !(isTrailingEmpty && hasNonEmpty);
      if (shouldInsertBreak) {
        nodes.push({ type: 'hardBreak' });
      }
      if (segment.length > 0) {
        nodes.push({
          type: 'text',
          text: segment,
          ...(marks ? { marks } : {}),
        });
      }
    });
    return nodes;
  };

  const transformChildren = (children: JSONContent[]): JSONContent[] =>
    children.flatMap((child) => {
      if (child.type === 'text' && typeof child.text === 'string') {
        if (!child.text.includes('\n')) {
          // No newline to normalize; return child unchanged
          return [child];
        }
        const segments = child.text.split('\n');
        // Split on newline and interleave hardBreak nodes
        return toTextAndBreakNodes(segments, child.marks);
      }
      if (Array.isArray(child.content) && child.content.length > 0) {
        // Recurse only when child.content is a non-empty array
        return [{ ...child, content: transformChildren(child.content) }];
      }
      // Leaf nodes or nodes without content: return unchanged
      return [child];
    });

  // Root must have non-empty array content to transform; otherwise return unchanged
  if (!Array.isArray(doc.content) || doc.content.length === 0) return doc;
  return { ...doc, content: transformChildren(doc.content) };
}
